import { useState, useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Popup, Toast } from 'react-vant'
import Big from 'big.js'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomButton from '@/components/CustomButton'
import FixedBottom from '@/components/FixedBottom'
import CurrencySelectPopup, { type Currency } from '@/components/CurrencySelectPopup'
import { useCurrencyList } from '@/hooks'
import { formatThousands, generateIdempotencyKey } from '@/utils'
import Divider from '@/components/Divider'
import { APIWeb } from '@/api'

export default function RechargePage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const cardId = searchParams.get('id') || ''
    const cardNo = searchParams.get('cardNo') || ''
    const { currencyList } = useCurrencyList()
    const [loading, setLoading] = useState(false)

    // TODO: 手续费需要从配置获取
    const fee = 0 // 手续费1%

    // 充值金额
    const [amount, setAmount] = useState('')
    // 选中的币种
    const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(null)
    // 币种选择弹窗
    const [currencyPopupVisible, setCurrencyPopupVisible] = useState(false)
    // 确认弹窗
    const [confirmPopupVisible, setConfirmPopupVisible] = useState(false)

    // 当前选中的币种（默认选择第一个）
    const currentCurrency = useMemo(() => {
        if (selectedCurrency) {
            return selectedCurrency
        }
        // 从 currencyList 转换为 Currency 格式
        const first = currencyList[0]
        return first ? { icon: first.icon, name: first.name, description: first.description, symbol: first.symbol } : null
    }, [selectedCurrency, currencyList])

    // 获取当前币种余额（从真实用户数据）
    const currentBalance = useMemo(() => {
        if (!currentCurrency) return 0
        const coinInfo = currencyList.find(c => c.symbol === currentCurrency.symbol)
        return parseFloat(coinInfo?.balance || '0')
    }, [currentCurrency, currencyList])

    // 处理金额输入
    const handleAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        if (/^\d*\.?\d{0,2}$/.test(value) || value === '') {
            setAmount(value)
        }
    }, [])

    // 计算到账金额（格式化显示）
    const receivedAmount = useMemo(() => {
        if (!amount) return '0.00'
        const num = parseFloat(amount) || 0
        return formatThousands(num, 2, true)
    }, [amount])

    // 计算扣除手续费后的实际到账金额（使用 big.js 精确计算）
    const actualReceivedAmount = useMemo(() => {
        if (!amount) return '0.00'
        try {
            const amountBig = new Big(amount || 0)
            const feeBig = new Big(fee || 0)
            const result = amountBig.minus(feeBig)
            // 确保结果不为负数
            return result.lt(0) ? '0.00' : result.toFixed(2)
        } catch {
            return '0.00'
        }
    }, [amount, fee])

    // 检查是否可以充值
    const canRecharge = useMemo(() => {
        const num = parseFloat(amount) || 0
        return num > 0 && num <= currentBalance
    }, [amount, currentBalance])

    // 点击充值按钮，显示确认弹窗
    const handleRechargeClick = useCallback(() => {
        if (!canRecharge || !currentCurrency) return
        setConfirmPopupVisible(true)
    }, [canRecharge, currentCurrency])

    // 确认充值，直接调用 API
    const handleConfirmRecharge = useCallback(async () => {
        if (!canRecharge || !currentCurrency || !cardId) return

        try {
            setLoading(true)
            
            // 调用充值 API
            await APIWeb.virtualCardRechargeV2({
                card_id: Number(cardId),
                amount: parseFloat(amount),
                pay_type: currentCurrency.symbol.toLowerCase() as PayType,
                idempotency_key: generateIdempotencyKey()
            })

            setConfirmPopupVisible(false)
            
            // 跳转到充值成功页面
            navigate('/card-v2/recharge/success', { replace: true })
        } catch (error: any) {
            console.error('充值失败:', error)
            Toast.fail(error?.message || t('充值失败'))
        } finally {
            setLoading(false)
        }
    }, [cardId, amount, currentCurrency, canRecharge, navigate])

    // 选择币种
    const handleSelectCurrency = useCallback((currency: Currency) => {
        setSelectedCurrency(currency)
        setAmount('')
    }, [])

    return (
        <PageLayout>
            <Header
                leftChildren={<Back />}
                centerChildren={<span className="text-base font-medium">{t('充值')}</span>}
            />

            <div className="flex-1 flex flex-col px-5 pb-8">
                {/* 充值金额输入 */}
                <div className="mt-6">
                    <div className="text-sm text-[#1D2129] font-medium mb-3">
                        {t('充值金额')}
                    </div>
                    <div className="flex items-center justify-between border-b border-[#E5E6EB] pb-4">
                        <input
                            type="text"
                            inputMode="decimal"
                            className="w-0 flex-1 min-w-0 text-8 font-semibold text-[#C9CDD4] bg-transparent outline-none placeholder-[#C9CDD4]"
                            value={amount}
                            onChange={handleAmountChange}
                            maxLength={18}
                            placeholder="0.00"
                            style={{ color: amount ? '#1D2129' : '#C9CDD4' }}
                        />
                        {currentCurrency && (
                            <div
                                className="flex-shrink-0 flex items-center bg-[#F7F8FA] rounded-full px-3 py-2 cursor-pointer ml-3"
                                onClick={() => setCurrencyPopupVisible(true)}
                            >
                                <div className="w-5 h-5 rounded-full mr-2 flex items-center justify-center overflow-hidden">
                                    <img src={currentCurrency.icon} alt={currentCurrency.symbol} className="w-full h-full object-cover" />
                                </div>
                                <span className="text-sm font-medium text-[#1D2129] mr-2">{currentCurrency.symbol}</span>
                                <img src="/images/card-v2/select-down.svg" alt="down" />
                            </div>
                        )}
                    </div>
                    <div className="text-sm text-[#86909C] mt-2">
                        {t('可用')}  {formatThousands(currentBalance, 2, true)} {currentCurrency?.symbol}
                    </div>
                </div>
                <Divider type="solid" className='mt-4' />

                {/* 到账金额 */}
                <div className="mt-8">
                    <div className="text-sm text-[#1D2129] font-medium mb-3">
                        {t('到账金额')}
                    </div>
                    <div className="flex items-center justify-between border-b border-[#E5E6EB] pb-4">
                        <div className="text-8 font-semibold text-ellipsis overflow-hidden" style={{ color: amount ? '#1D2129' : '#C9CDD4' }}>
                            {formatThousands(actualReceivedAmount, 2, true)}
                        </div>
                        <div className="flex items-center bg-[#F7F8FA] rounded-full px-3 py-2">
                            <div className="w-5 h-5 rounded-full bg-[#E5E6EB] mr-2 flex items-center justify-center overflow-hidden">
                                <img src="/images/home/usd.png" alt="USD" className="w-full h-full object-cover" />
                            </div>
                            <span className="text-sm font-medium text-[#1D2129]">USD</span>
                        </div>
                    </div>
                </div>
                <Divider type="solid" className='mt-4' />

                {/* 充值按钮 */}
                <FixedBottom>
                    <CustomButton
                        onClick={handleRechargeClick}
                        disabled={!canRecharge}
                    >
                        {t('充值')}
                    </CustomButton>
                </FixedBottom>
            </div>

            {/* 币种选择弹窗 */}
            <CurrencySelectPopup
                visible={currencyPopupVisible}
                onClose={() => setCurrencyPopupVisible(false)}
                onSelect={handleSelectCurrency}
                currencies={currencyList}
            />

            {/* 充值确认弹窗 */}
            <Popup
                visible={confirmPopupVisible}
                onClose={() => setConfirmPopupVisible(false)}
                position="bottom"
                round
                closeable
                closeIcon={<img src="/icon/close.svg" alt="" />}
                closeIconPosition="top-right"
            >
                <div className="px-5 pt-6 pb-8">
                    <div className="text-lg font-semibold text-[#1D2129] mb-8">
                        {t('信息确认')}
                    </div>

                    <div className="flex flex-col items-center mb-8">
                        <div className="w-16 h-16 rounded-full bg-[#F7F8FA] flex items-center justify-center mb-4">
                            <img src="/images/card-v2/recharge-confirm.svg" alt="" />
                        </div>
                        <div className="text-4xl font-semibold text-[#1D2129]">
                            -{formatThousands(parseFloat(amount) || 0, 2, true)}
                        </div>
                    </div>

                    <div className="space-y-4 mb-8">
                        <div className="flex items-center justify-between">
                            <span className="text-base text-[#86909C]">{t('充值币种')}</span>
                            <span className="text-base font-medium text-[#1D2129]">{currentCurrency?.symbol}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-base text-[#86909C]">{t('到账')}</span>
                            <span className="text-base font-medium text-[#1D2129]">{formatThousands(actualReceivedAmount, 2, true)} USD</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-base text-[#86909C]">{t('手续费')}</span>
                            <span className="text-base font-medium text-[#1D2129]">0 USD</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-base text-[#86909C]">{t('充值卡号')}</span>
                            <span className="text-base font-medium text-[#1D2129]">{cardNo || '--'}</span>
                        </div>
                    </div>

                    <CustomButton
                        onClick={handleConfirmRecharge}
                        loading={loading}
                    >
                        {t('确定')}
                    </CustomButton>
                </div>
            </Popup>
        </PageLayout>
    )
}
